import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useSiteSettings } from "@/lib/settings";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LogOut, LayoutDashboard, Upload, FileText, Calendar, Palette, Users, Settings,
  Beaker, Sparkles, Cable, BarChart3, ShieldCheck, CreditCard, Mail, Package as PackageIcon, Building2, ClipboardList, TrendingUp, Gift, Star, Wallet as WalletIcon,
  Menu, X, LayoutTemplate, BookOpen,
} from "lucide-react";

/**
 * Shared responsive nav shell used by every logged-in dashboard and the
 * public marketing site. Both TopNav and DashboardLayout expose a mobile
 * drawer (Sheet) so users on <md screens still get full navigation.
 */

const PUBLIC_NAV_ITEMS = [
  { key: "home", to: "/", label: "Home", testid: "nav-home-link", flag: "show_home_link" },
  { key: "book", to: "/book-tests", label: "Book Tests", testid: "nav-booktests-link", flag: "show_book_tests_link", requiresMarketplace: true },
  { key: "how", to: "/#how", label: "How it works", testid: "nav-how-link", flag: "show_how_it_works_link", external: true },
  { key: "labs", to: "/for-labs", label: "For Labs", testid: "nav-forlabs-link", flag: "show_for_labs_btn" },
];

function useNavFlags() {
  const { platform } = useSiteSettings();
  const showMarketplace = platform?.marketplace_enabled !== false;
  return {
    showBookCta: platform?.show_book_test_btn !== false && showMarketplace,
    showDashboard: platform?.show_dashboard_btn !== false,
    showLogin: platform?.show_login_link !== false,
    showMarketplace,
    items: PUBLIC_NAV_ITEMS.filter((it) => (platform?.[it.flag] !== false) && (!it.requiresMarketplace || showMarketplace)),
  };
}

export function TopNav({ transparent = false }) {
  const { user, logout } = useAuth();
  const { settings } = useSiteSettings();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const logo = settings?.logo_url || "/efgh-logo.png";
  const bookLabel = settings?.nav_book_label || "Book a Test";
  const bookUrl = settings?.nav_book_url || "/book-tests";
  const isExternal = /^https?:\/\//i.test(bookUrl);
  const { showBookCta, showDashboard, showLogin, items } = useNavFlags();
  const dashboardHref = user?.role === "patient" ? "/patient" : user?.role === "lab_admin" ? "/lab" : "/admin";

  const bookInner = (
    <Button data-testid="nav-book-btn" size="sm" className="bg-efgh-orange hover:bg-efgh-orange/90 text-white shadow-sm">
      <Calendar className="w-4 h-4 mr-1.5" /> {bookLabel}
    </Button>
  );

  return (
    <header data-testid="topnav" className={`sticky top-0 z-40 ${transparent ? "" : "bg-white/90 backdrop-blur border-b border-border"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-2">
        <Link to="/" data-testid="topnav-logo" className="flex items-center shrink-0">
          <img src={logo} alt="EFGH — everything for good health" className="h-9 sm:h-12 md:h-14" />
        </Link>

        {/* Desktop nav links */}
        <nav className="hidden md:flex items-center gap-6 text-sm text-ink/80" data-testid="topnav-desktop-links">
          {items.map((it) => (
            it.external
              ? <a key={it.key} href={it.to} className="hover:text-ink" data-testid={it.testid}>{it.label}</a>
              : <Link key={it.key} to={it.to} className="hover:text-ink" data-testid={it.testid}>{it.label}</Link>
          ))}
        </nav>

        {/* Right-side actions */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {showBookCta && (isExternal ? (
            <a href={bookUrl} target="_blank" rel="noreferrer" className="hidden sm:inline-flex">{bookInner}</a>
          ) : (
            <Link to={bookUrl} className="hidden sm:inline-flex">{bookInner}</Link>
          ))}
          {user ? (
            <>
              {showDashboard && (
                <Button data-testid="dashboard-nav-btn" variant="outline" size="sm" onClick={() => nav(dashboardHref)} className="hidden sm:inline-flex">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                </Button>
              )}
              <Button data-testid="logout-btn" variant="ghost" size="icon" onClick={() => { logout(); nav("/"); }} aria-label="Sign out" className="hidden sm:inline-flex">
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : showLogin ? (
            <>
              <Link to="/login" className="hidden sm:inline-flex"><Button data-testid="nav-login-btn" variant="ghost" size="sm">Login</Button></Link>
              <Link to="/register" className="hidden sm:inline-flex"><Button data-testid="nav-register-btn" variant="outline" size="sm">Sign up</Button></Link>
            </>
          ) : null}

          {/* Mobile hamburger — opens a Sheet with every link */}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border border-border bg-white hover:bg-muted"
                aria-label="Open menu"
                data-testid="mobile-menu-toggle"
              >
                <Menu className="w-5 h-5 text-ink" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86vw] max-w-sm p-0 flex flex-col" data-testid="mobile-menu-sheet">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <img src={logo} alt="EFGH" className="h-9" />
                <button type="button" onClick={() => setOpen(false)} className="w-9 h-9 rounded-lg hover:bg-muted grid place-items-center" aria-label="Close menu"><X className="w-4 h-4" /></button>
              </div>
              <div className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
                {items.map((it) => (
                  it.external
                    ? <a key={it.key} href={it.to} onClick={() => setOpen(false)} className="block px-3 py-3 rounded-lg text-sm text-ink hover:bg-muted" data-testid={`mobile-${it.testid}`}>{it.label}</a>
                    : <Link key={it.key} to={it.to} onClick={() => setOpen(false)} className="block px-3 py-3 rounded-lg text-sm text-ink hover:bg-muted" data-testid={`mobile-${it.testid}`}>{it.label}</Link>
                ))}
              </div>
              <div className="border-t border-border p-4 space-y-2">
                {showBookCta && (isExternal ? (
                  <a href={bookUrl} target="_blank" rel="noreferrer" onClick={() => setOpen(false)}>
                    <Button className="w-full bg-efgh-orange hover:bg-efgh-orange/90 text-white" data-testid="mobile-nav-book-btn">
                      <Calendar className="w-4 h-4 mr-2" /> {bookLabel}
                    </Button>
                  </a>
                ) : (
                  <Link to={bookUrl} onClick={() => setOpen(false)}>
                    <Button className="w-full bg-efgh-orange hover:bg-efgh-orange/90 text-white" data-testid="mobile-nav-book-btn">
                      <Calendar className="w-4 h-4 mr-2" /> {bookLabel}
                    </Button>
                  </Link>
                ))}
                {user ? (
                  <>
                    {showDashboard && (
                      <Button variant="outline" className="w-full" onClick={() => { setOpen(false); nav(dashboardHref); }} data-testid="mobile-dashboard-btn">
                        <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                      </Button>
                    )}
                    <Button variant="ghost" className="w-full" onClick={() => { setOpen(false); logout(); nav("/"); }} data-testid="mobile-logout-btn">
                      <LogOut className="w-4 h-4 mr-2" /> Sign out
                    </Button>
                  </>
                ) : showLogin ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Link to="/login" onClick={() => setOpen(false)}><Button variant="outline" className="w-full" data-testid="mobile-nav-login-btn">Login</Button></Link>
                    <Link to="/register" onClick={() => setOpen(false)}><Button className="w-full bg-ink text-white hover:bg-ink/90" data-testid="mobile-nav-register-btn">Sign up</Button></Link>
                  </div>
                ) : null}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

export function SideNav({ items }) {
  const { pathname } = useLocation();
  return (
    <aside className="hidden md:block w-60 shrink-0 border-r border-border bg-white min-h-[calc(100vh-4rem)] py-6" data-testid="sidenav-desktop">
      <SideNavItems items={items} pathname={pathname} />
    </aside>
  );
}

function SideNavItems({ items, pathname, onNavigate }) {
  return (
    <nav className="flex flex-col gap-0.5 px-3">
      {items.map((it) => {
        if (it.section) return <div key={it.section} className="text-[10px] uppercase tracking-[0.22em] text-ink/40 font-heading font-semibold mt-4 mb-1.5 px-3">{it.section}</div>;
        const active = pathname === it.to;
        const Icon = it.icon;
        return (
          <Link
            key={it.to}
            to={it.to}
            onClick={onNavigate}
            data-testid={`side-${it.testId}`}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${active ? "bg-ink text-white font-medium" : "text-ink/70 hover:bg-muted"}`}
          >
            <Icon className="w-4 h-4" /> {it.label}
          </Link>
        );
      })}
    </nav>
  );
}

/**
 * Mobile sidebar drawer — button lives inside the dashboard scroll area's
 * sticky sub-header (below TopNav) so it's always accessible.
 */
function MobileSidebarButton({ items, label }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const active = items.find((it) => !it.section && it.to === pathname);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          type="button"
          className="md:hidden inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-border bg-white shadow-sm hover:bg-muted text-sm text-ink"
          data-testid="mobile-sidenav-toggle"
        >
          <Menu className="w-4 h-4" />
          <span className="font-medium truncate max-w-[10rem]">{active?.label || label}</span>
        </button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[82vw] max-w-xs p-0 flex flex-col" data-testid="mobile-sidenav-sheet">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="text-[11px] uppercase tracking-[0.22em] text-ink/50 font-heading font-semibold">{label}</div>
          <button type="button" onClick={() => setOpen(false)} className="w-8 h-8 rounded-lg hover:bg-muted grid place-items-center" aria-label="Close"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto py-3">
          <SideNavItems items={items} pathname={pathname} onNavigate={() => setOpen(false)} />
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function DashboardLayout({ children, role }) {
  const patientItems = [
    { to: "/patient", label: "Overview", icon: LayoutDashboard, testId: "overview" },
    { to: "/patient/upload", label: "Upload Report", icon: Upload, testId: "upload" },
    { to: "/patient/reports", label: "My Reports", icon: FileText, testId: "reports" },
    { to: "/patient/trends", label: "Health Trends", icon: TrendingUp, testId: "trends" },
    { to: "/patient/prescriptions", label: "Prescriptions", icon: ClipboardList, testId: "prescriptions" },
    { to: "/patient/bookings", label: "My Bookings", icon: Calendar, testId: "bookings" },
    { to: "/patient/family", label: "Family Members", icon: Users, testId: "family" },
    { to: "/patient/referrals", label: "Refer & Earn", icon: Gift, testId: "referrals" },
    { to: "/patient/wallet", label: "Wallet", icon: WalletIcon, testId: "wallet" },
  ];
  // Patient bottom tab bar — the 4 most-used destinations + "More" opens the full drawer.
  const patientTabs = [
    { to: "/patient",         label: "Home",     icon: LayoutDashboard, testId: "tab-home" },
    { to: "/patient/upload",  label: "Upload",   icon: Upload,          testId: "tab-upload" },
    { to: "/patient/reports", label: "Reports",  icon: FileText,        testId: "tab-reports" },
    { to: "/patient/wallet",  label: "Wallet",   icon: WalletIcon,      testId: "tab-wallet" },
  ];
  const labItems = [
    { to: "/lab", label: "Dashboard", icon: LayoutDashboard, testId: "overview" },
    { to: "/lab/reports", label: "Reports", icon: FileText, testId: "reports" },
    { to: "/lab/patients", label: "Patients", icon: Users, testId: "patients" },
    { to: "/lab/bookings", label: "Bookings", icon: Calendar, testId: "bookings" },
    { section: "Configure" },
    { to: "/lab/branding", label: "Branding", icon: Palette, testId: "branding" },
    { to: "/lab/catalogue", label: "Test Catalogue", icon: Beaker, testId: "catalogue" },
    { to: "/lab/packages", label: "Packages", icon: PackageIcon, testId: "packages" },
    { to: "/lab/rules", label: "Recommendation Rules", icon: Sparkles, testId: "rules" },
    { to: "/lab/lis", label: "LIS Connection", icon: Cable, testId: "lis" },
    { to: "/lab/email-intake", label: "Email Intake", icon: Mail, testId: "email-intake" },
    { section: "Insights" },
    { to: "/lab/analytics", label: "Analytics", icon: BarChart3, testId: "analytics" },
    { to: "/lab/reviews", label: "Patient Reviews", icon: Star, testId: "reviews" },
    { to: "/lab/referrals", label: "Referrals", icon: Gift, testId: "referrals" },
    { section: "Account" },
    { to: "/lab/team", label: "Team", icon: ShieldCheck, testId: "team" },
    { to: "/lab/billing", label: "Subscription", icon: CreditCard, testId: "billing" },
    { to: "/lab/settings", label: "Settings", icon: Settings, testId: "settings" },
  ];
  const adminItems = [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, testId: "overview" },
    { section: "Operations" },
    { to: "/admin/labs", label: "Empanelled Labs", icon: Building2, testId: "labs" },
    { to: "/admin/users", label: "Users", icon: Users, testId: "users" },
    { to: "/admin/reports", label: "All Reports", icon: FileText, testId: "reports" },
    { to: "/admin/bookings", label: "All Bookings", icon: Calendar, testId: "bookings" },
    { section: "Marketplace" },
    { to: "/admin/marketplace-control", label: "Visibility control", icon: ShieldCheck, testId: "marketplace-control" },
    { to: "/admin/marketing", label: "Marketing site", icon: Sparkles, testId: "marketing" },
    { section: "Monetisation" },
    { to: "/admin/pricing", label: "Pricing", icon: CreditCard, testId: "pricing" },
    { to: "/admin/billing", label: "Billing & payouts", icon: CreditCard, testId: "billing" },
    { section: "System" },
    { to: "/admin/letterhead-templates", label: "Letterhead templates", icon: LayoutTemplate, testId: "letterhead-templates" },
    { to: "/admin/knowledge-base", label: "Knowledge base", icon: BookOpen, testId: "knowledge-base" },
    { to: "/admin/platform-settings", label: "Platform settings", icon: Settings, testId: "platform-settings" },
  ];
  const items = role === "patient" ? patientItems : role === "lab_admin" ? labItems : adminItems;
  const label = role === "patient" ? "Menu" : role === "lab_admin" ? "Lab Menu" : "Admin Menu";
  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      <TopNav />
      {/* Mobile-only sub-header — hidden for patient because bottom tab bar handles nav */}
      {role !== "patient" && (
        <div className="md:hidden sticky top-16 z-30 bg-[#F7F8FA]/95 backdrop-blur border-b border-border/60 px-4 py-2">
          <MobileSidebarButton items={items} label={label} />
        </div>
      )}
      <div className="max-w-7xl mx-auto flex">
        <SideNav items={items} />
        <main className={`flex-1 p-4 sm:p-6 lg:p-8 min-w-0 ${role === "patient" ? "pb-24 md:pb-8" : ""}`}>{children}</main>
      </div>
      {role === "patient" && <PatientBottomTabs tabs={patientTabs} moreItems={patientItems} />}
    </div>
  );
}

/**
 * PatientBottomTabs — mobile-only bottom navigation (Apple Health / WHOOP style).
 * Shows the 4 core destinations + a "More" button that opens the full drawer.
 * Hidden on ≥md screens where the sidebar is already visible.
 */
function PatientBottomTabs({ tabs, moreItems }) {
  const { pathname } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  return (
    <>
      <nav
        data-testid="patient-bottom-tabs"
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-border pb-[env(safe-area-inset-bottom)]"
      >
        <div className="grid grid-cols-5">
          {tabs.map((t) => {
            const active = pathname === t.to;
            const Icon = t.icon;
            return (
              <Link
                key={t.to}
                to={t.to}
                data-testid={t.testId}
                className={`flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10.5px] transition-colors ${active ? "text-ink" : "text-ink/50 hover:text-ink"}`}
              >
                <Icon className={`w-5 h-5 ${active ? "text-brand" : ""}`} strokeWidth={active ? 2.4 : 1.8} />
                <span className={active ? "font-semibold" : ""}>{t.label}</span>
              </Link>
            );
          })}
          <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                data-testid="tab-more"
                className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10.5px] text-ink/50 hover:text-ink"
              >
                <Menu className="w-5 h-5" strokeWidth={1.8} />
                <span>More</span>
              </button>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-3xl p-0 max-h-[80vh]" data-testid="patient-more-sheet">
              <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border">
                <div className="font-heading font-semibold text-ink">More</div>
                <button type="button" onClick={() => setMoreOpen(false)} className="w-8 h-8 rounded-lg hover:bg-muted grid place-items-center" aria-label="Close"><X className="w-4 h-4" /></button>
              </div>
              <div className="p-3 grid grid-cols-3 gap-2">
                {moreItems.map((it) => {
                  if (it.section) return null;
                  const Icon = it.icon;
                  const active = pathname === it.to;
                  return (
                    <Link
                      key={it.to}
                      to={it.to}
                      onClick={() => setMoreOpen(false)}
                      data-testid={`more-${it.testId}`}
                      className={`flex flex-col items-center gap-2 p-4 rounded-2xl border transition-all ${active ? "border-ink bg-ink/5" : "border-border hover:bg-muted"}`}
                    >
                      <div className={`w-11 h-11 rounded-xl grid place-items-center ${active ? "bg-ink text-white" : "bg-muted text-ink/80"}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="text-[11.5px] text-center text-ink font-medium leading-tight">{it.label}</div>
                    </Link>
                  );
                })}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </>
  );
}
